import type {EditorState} from '@codemirror/state'
import type {EditorView} from '@codemirror/view'

/** Returns true if there is actual selected text in the editor. */
export function hasNonEmptySelection(state: EditorState): boolean {
  return state.selection.ranges.length === 1 && !state.selection.main.empty
}

export function expandRangeToLineEnds(state: EditorState) {
  const caretLocation = state.selection.main.anchor
  const document = state.doc.toString()

  const lineFrom = document.slice(0, caretLocation).lastIndexOf('\n') + 1
  let lineTo = document.indexOf('\n', caretLocation)
  if (lineTo === -1) lineTo = document.length

  return {from: lineFrom, to: lineTo}
}

/**
 * Find word boundaries around a given position
 */
export function findWordBoundaries(text: string, position: number): {start: number; end: number} {
  let start = position
  let end = position

  // Find word start by moving backwards until whitespace or beginning
  while (start > 0 && text[start - 1] && !/\s/.test(text[start - 1]!)) {
    start--
  }
  // Find word end by moving forwards until whitespace or end
  while (end < text.length && text[end] && !/\s/.test(text[end]!)) {
    end++
  }

  return {start, end}
}

export function getCaretPosition(state: EditorState): number | null {
  return state.selection.ranges.length === 1 && state.selection.main.empty ? state.selection.main.anchor : null
}

/**
 * Get accurate coordinates for a caret position, handling decorated blocks correctly.
 * Falls back to CodeMirror's `coordsAtPos` if DOM range approach fails.
 */
export const getAccurateCaretCoords = (
  view: EditorView,
  pos: number,
): {left: number; top: number; right: number; bottom: number} | null => {
  try {
    // Try using DOM range for more accurate positioning in decorated blocks
    const domPos = view.domAtPos(pos)
    if (domPos?.node) {
      try {
        const range = document.createRange()
        range.setStart(domPos.node, domPos.offset)
        range.setEnd(domPos.node, domPos.offset)
        const rect = range.getBoundingClientRect()

        // Return rect if it has valid coordinates (not all zeros from hidden nodes)
        if (rect.left !== 0 || rect.right !== 0 || rect.top !== 0 || rect.bottom !== 0) {
          return {left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom}
        }
      } catch {
        // range.setStart/setEnd throws if offset is outside the node size
      }
    }

    // Fall back to CodeMirror's coordsAtPos method
    return view.coordsAtPos(pos) ?? null
  } catch {
    return null
  }
}
